/**
 * useAuthStorageSync
 *
 * Ends, or swaps, this tab's session when another tab changes the account.
 *
 * Nothing used to coordinate tabs: a tab left open as account A kept its
 * socket and its stores while another tab signed A out and B in, and kept
 * writing A's state into the storage keys B's tab reads. There is no
 * protocol to add for this. zustand already writes the persisted auth store
 * to localStorage on every change, and the browser fires `storage` in every
 * OTHER tab when it does — so this tab only has to notice that the stored
 * user is no longer its own and follow: to nobody (A signed out elsewhere)
 * or to B (B signed in elsewhere). Either way the store's own subscription
 * crosses the session boundary here too, and every account-scoped cache in
 * this tab clears the same way it would have in the tab that acted.
 *
 * Adopting B rather than ending the session is the truthful outcome under
 * session auth: the cookie is shared, so once the other tab is B, this tab
 * already is on the server.
 *
 * This replaces the never-mounted `useTabSync`, which was a broadcast
 * protocol every sign-out path had to remember to call.
 */

import { useEffect } from 'react';

import { authStore } from '@personal-trainer-app/shared/src/store/auth.store';

import type { User } from '@personal-trainer-app/shared/src/types/auth';

/** What zustand's persist middleware writes under the auth key. */
interface PersistedAuth {
  state?: { user?: User | null };
}

const storedUser = (value: string | null): User | null => {
  if (!value) return null;
  try {
    return (JSON.parse(value) as PersistedAuth).state?.user ?? null;
  } catch {
    return null;
  }
};

export function useAuthStorageSync(): void {
  useEffect(() => {
    const authKey = authStore.persist.getOptions().name;

    const onStorage = (event: StorageEvent): void => {
      if (event.key !== authKey) return;
      if (event.storageArea && event.storageArea !== localStorage) return;

      const next = storedUser(event.newValue);
      const mine = authStore.getState().user?.username ?? null;
      if ((next?.username ?? null) === mine) return;

      // Local only: the other tab has already told the server.
      if (next) authStore.getState().setUser(next);
      else authStore.getState().clearUser();
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
}
